import { Button, DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import BulkVoucherListPage from "@saleor/discounts/components/BulkVoucherListPage";
import { TypedVoucherBulkDeactivate } from "@saleor/discounts/mutations";
import { VoucherBulkDeactivate } from "@saleor/discounts/types/VoucherBulkDeactivate";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useDebounce from "@saleor/hooks/useDebounce";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  useBulkVoucherListQuery,
  useVoucherGroupListQuery,
  useVoucherListQuery
} from "../../queries";
import {
  bulkVoucherListUrl,
  BulkVoucherListUrlDialog,
  BulkVoucherListUrlFilters,
  BulkVoucherListUrlQueryParams,
  VoucherListUrlQueryParams
} from "../../urls";
import { getFilterVariables } from "./filter";
import { getSortQueryVariables } from "./sort";
interface BulkVoucherListProps {
  params: VoucherListUrlQueryParams;
  id: string;
}

export const BulkVoucherList: React.FC<BulkVoucherListProps> = ({
  params,
  id
}) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const intl = useIntl();
  const shop = useShop();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const { updateListSettings, settings } = useListSettings(
    ListViews.BULK_VOUCHER_LIST
  );

  const canOpenBulkActionDialog = maybe(() => params.ids.length > 0);

  const paginationState = createPaginationState(settings.rowNumber, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      id,
      sort: getSortQueryVariables(params)
    }),
    [params]
  );

  const { data, loading, refetch } = useBulkVoucherListQuery({
    displayLoader: true,
    variables: queryVariables
  });
  const currencySymbol = maybe(() => shop.defaultCurrency, "PHP");

  const [openModal, closeModal] = createDialogActionHandlers<
    BulkVoucherListUrlDialog,
    BulkVoucherListUrlQueryParams
  >(navigate, params => bulkVoucherListUrl(id, params), params);

  const handleVoucherBulkDeactivate = (data: VoucherBulkDeactivate) => {
    if (data.voucherBulkDeactivate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Voucher Deactivated."
        })
      });
      reset();
      closeModal();
      refetch();
    } else if (data.voucherBulkDeactivate.errors.length !== 0) {
      closeModal();
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Unable to deactivate. Voucher promo is ongoing."
        })
      });
      reset();
      refetch();
    }
  };

  const { reset: resetSelected } = useBulkActions(params.ids);
  const changeFilterField = (filter: BulkVoucherListUrlFilters) => {
    resetSelected();
    navigate(
      bulkVoucherListUrl(encodeURIComponent(id), {
        ...filter,
        activeTab: params.activeTab
      })
    );
  };

  const { data: searchData } = useVoucherListQuery({
    variables: {
      ...queryVariables,
      filter: {
        ...queryVariables.filter,
        groupId: id
      }
    }
  });

  const handleSearchChange = (value: string) => {
    const filters = { ...params, query: value };
    changeFilterField(filters);
  };

  const debouncedSearchChange = useDebounce(handleSearchChange, 50);

  const vouchers = params.query
    ? maybe(() => searchData?.vouchers?.edges?.map(edge => edge.node))
    : maybe(() => data?.voucherGroup?.vouchers?.edges?.map(edge => edge.node));

  const { data: voucherGroupCodeData } = useVoucherGroupListQuery({
    variables: {
      id
    }
  });

  const voucherSearchInactive = maybe(
    () => data?.voucherGroup?.vouchers?.pageInfo
  );
  const voucherSearchActive = maybe(() => searchData?.vouchers?.pageInfo);
  const pageInfo = params.query ? voucherSearchActive : voucherSearchInactive;

  const { loadNextPage, loadPreviousPage } = paginate(
    pageInfo,
    paginationState,
    params
  );

  return (
    <TypedVoucherBulkDeactivate onCompleted={handleVoucherBulkDeactivate}>
      {(voucherBulkDeactivate, voucherBulkDeactivateOpts) => {
        const onVoucherBulkDeactivate = () =>
          voucherBulkDeactivate({
            variables: {
              ids: params.ids
            }
          });
        return (
          <>
            <BulkVoucherListPage
              params={params}
              paginationState={paginationState}
              bulkID={data?.voucherGroup?.id}
              currencySymbol={currencySymbol}
              vouchers={vouchers}
              disabled={loading}
              pageInfo={pageInfo}
              onNextPage={loadNextPage}
              onPreviousPage={loadPreviousPage}
              onUpdateListSettings={updateListSettings}
              onRowClick={id => () => navigate(bulkVoucherListUrl(id))}
              onAdd={undefined}
              defaultCurrency={maybe(() => shop.defaultCurrency)}
              brands={undefined}
              isChecked={isSelected}
              selected={listElements.length}
              toggle={toggle}
              toggleAll={toggleAll}
              toolbar={
                <Button
                  color="primary"
                  onClick={() =>
                    openModal("deactivate", {
                      ids: listElements
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="Deactivate"
                    description="deactivate button"
                  />
                </Button>
              }
              bulkVoucher={maybe(() => voucherGroupCodeData.voucherGroup.code)}
              filterOpts={undefined}
              onFilterChange={undefined}
              initialSearch={params.query || ""}
              onSearchChange={debouncedSearchChange}
              currentTab={0}
              tabs={[]}
              onTabChange={undefined}
              onTabDelete={undefined}
              onTabSave={undefined}
            />
            <ActionDialog
              confirmButtonState={voucherBulkDeactivateOpts.status}
              onClose={closeModal}
              onConfirm={onVoucherBulkDeactivate}
              open={params.action === "deactivate" && canOpenBulkActionDialog}
              title={intl.formatMessage({
                defaultMessage: "Deactivate Vouchers",
                description: "dialog header"
              })}
              variant="delete"
              confirmButtonLabel="Deactivate"
            >
              {canOpenBulkActionDialog && (
                <DialogContentText>
                  <FormattedMessage
                    defaultMessage="{counter,plural,one{Are you sure you want to deactivate this voucher?} other{Are you sure you want to deactivate {displayQuantity} vouchers?}}"
                    description="dialog content"
                    values={{
                      counter: params.ids.length,
                      displayQuantity: <strong>{params.ids.length}</strong>
                    }}
                  />
                </DialogContentText>
              )}
            </ActionDialog>
          </>
        );
      }}
    </TypedVoucherBulkDeactivate>
  );
};
export default BulkVoucherList;
